import AHDate from "_shared/dates/date";
import { OrderDirection } from "_shared/_api/ordering/order-direction";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import Guid from "_shared/guids/guid";
import { BoxType } from "notifications/values/box-type/box-type";
import NotificationFilterSchema, {
  type NotificationFilterJSON,
  type NotificationfilterParametersProps,
} from "notifications/entities/notification/api/request-contracts/notification-filter-schema";

export type NotificationQueryParams = {
  filterParameters: NotificationfilterParameters;
  orderParameters: NotificationorderParameters;
  paginationParameters: PaginationParameters;
};

export default class NotificationQuery {
  public readonly filterParameters: NotificationfilterParameters;
  public readonly orderParameters: NotificationorderParameters;
  public readonly paginationParameters: PaginationParameters;

  public constructor(params: NotificationQueryParams) {
    this.filterParameters = params.filterParameters;
    this.orderParameters = params.orderParameters;
    this.paginationParameters = params.paginationParameters;
  }

  public get searchParams(): URLSearchParams {
    return new URLSearchParams([
      ...this.paginationParameters.searchParams,
      ...this.orderParameters.searchParams,
      ...this.filterParameters.searchParams,
    ]);
  }

  public get cacheKey(): string {
    const params = this.searchParams;
    const sortedEntries = [...params.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    return sortedEntries.map(([k, v]) => `${k}=${v}`).join("&");
  }

  public isEqualTo(other?: NotificationQuery): boolean {
    if (!other) return false;
    return (
      this.filterParameters.isEqualTo(other.filterParameters) &&
      this.orderParameters.isEqualTo(other.orderParameters) &&
      this.paginationParameters.isEqualTo(other.paginationParameters)
    );
  }

  public toString(): string {
    return (
      `Filter Params: {\n${this.filterParameters.toString()}\n},\n` +
      `Order Params: {\n${this.orderParameters.toString()}\n},\n` +
      `Pagination Params: {\n${this.paginationParameters.toString()}\n}`
    );
  }
}

export class NotificationfilterParameters {
  public readonly query?: string;
  public readonly type?: string;
  public readonly from?: string;
  public readonly to?: string;
  public readonly description?: string;
  public readonly minCreatedDate?: AHDate;
  public readonly maxCreatedDate?: AHDate;
  public readonly isUnread?: boolean;
  public readonly boxType?: BoxType;
  public readonly isActionRequired?: boolean;
  public readonly isMessageNotification?: boolean;
  public readonly topicId?: Guid;

  public constructor(filters: NotificationfilterParametersProps) {
    this.query = filters?.query;
    this.type = filters?.type;
    this.from = filters?.from;
    this.to = filters?.to;
    this.description = filters?.description;
    this.minCreatedDate = filters?.minCreatedDate;
    this.maxCreatedDate = filters?.maxCreatedDate;
    this.isUnread = filters?.isUnread;
    this.boxType = filters?.boxType;
    this.isActionRequired = filters?.isActionRequired;
    this.isMessageNotification = filters?.isMessageNotification;
    this.topicId = filters?.topicId;
  }

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.query) searchParams.append("query", this.query);
    if (this.type) searchParams.append("type", this.type);
    if (this.from) searchParams.append("from", this.from);
    if (this.to) searchParams.append("to", this.to);
    if (this.description) searchParams.append("description", this.description);
    if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
    if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
    if (this.isUnread !== undefined) searchParams.append("isUnread", String(this.isUnread));
    if (this.boxType) searchParams.append("boxType", this.boxType);
    if (this.isActionRequired !== undefined) searchParams.append("isActionRequired", String(this.isActionRequired));
    if (this.isMessageNotification !== undefined) {
      searchParams.append("isMessageNotification", String(this.isMessageNotification));
    }
    if (this.topicId) searchParams.append("topicId", this.topicId.value);
    return searchParams;
  }

  public toJSON(): NotificationFilterJSON {
    return NotificationFilterSchema.serialize(this);
  }

  public isEqualTo(other: NotificationfilterParameters): boolean {
    if (!other) return false;
    return (
      (this.query === other.query &&
        this.type === other.type &&
        this.from === other.from &&
        this.to === other.to &&
        this.description === other.description &&
        this.minCreatedDate?.isEqualTo(other.minCreatedDate)) ||
      (!this.minCreatedDate && !other.minCreatedDate && this.maxCreatedDate?.isEqualTo(other.maxCreatedDate)) ||
      (!this.maxCreatedDate &&
        !other.maxCreatedDate &&
        this.isUnread === other.isUnread &&
        this.boxType === other.boxType &&
        this.isActionRequired === other.isActionRequired &&
        this.isMessageNotification === other.isMessageNotification &&
        this.topicId?.value === other.topicId?.value)
    );
  }

  public toString(): string {
    return `NotificationFilter {
      query: ${this.query},
      type: ${this.type},
      from: ${this.from},
      to: ${this.to},
      description: ${this.description},
      minCreatedDate: ${this.minCreatedDate},
      maxCreatedDate: ${this.maxCreatedDate},
      isUnread: ${this.isUnread},
      boxType: ${this.boxType},
      isActionRequired: ${this.isActionRequired},
      isMessageNotification: ${this.isMessageNotification},
      topicId: ${this.topicId}
    }`;
  }
}

export enum NotificationOrderField {
  From = "From",
  To = "To",
  CreatedDate = "CreatedDate",
}

export type NotificationorderParametersProps = {
  orderBy?: NotificationOrderField;
  orderDir?: OrderDirection;
};

export class NotificationorderParameters {
  public readonly orderBy?: NotificationOrderField;
  public readonly orderDir?: OrderDirection;

  public constructor(params: NotificationorderParametersProps) {
    this.orderBy = params.orderBy;
    this.orderDir = params.orderDir;
  }

  public get searchParams(): URLSearchParams {
    const searchParams = new URLSearchParams();
    if (this.orderBy) searchParams.append("orderBy", this.orderBy);
    if (this.orderDir) searchParams.append("orderDir", this.orderDir);
    return searchParams;
  }

  public isEqualTo(other: NotificationorderParameters): boolean {
    if (!other) return false;
    return this.orderBy === other.orderBy && this.orderDir === other.orderDir;
  }

  public toString(): string {
    return `NotificationOrder {
      orderBy: ${this.orderBy},
      orderDir: ${this.orderDir}
    }`;
  }
}
